import {
  DCKSLAP_RESOURCE_ADDRESS,
  GBOF_RESOURCE_ADDRESS,
  RADIX_TOKEN_RESOURCE_ADDRESS,
  REDDICKS_RESSOURCE_ADDRESS,
} from "../../../constants";
import { RADIX_CHARTS_URL } from "../../../config/siteContent";

export const DEFAULT_SWAP_BASE_RESOURCE = REDDICKS_RESSOURCE_ADDRESS;
export const SWAP_WIDGET_LSULP_RESOURCE_ADDRESS =
  "resource_rdx1thksg5ng70g9mmy9ne7wz0sc7auzrrwy7fmgcxzel2gvp8pj0xxfmf";
export const DEFAULT_SWAP_QUOTE_RESOURCE = SWAP_WIDGET_LSULP_RESOURCE_ADDRESS;

const SWAP_WIDGET_SYNC_TOKENS = {
  [REDDICKS_RESSOURCE_ADDRESS]: {
    resourceAddress: REDDICKS_RESSOURCE_ADDRESS,
    symbol: "DCKS",
    name: "REDDICKS",
    description:
      "REDDICKS (DCKS) is a community-driven cryptocurrency powering a gamified DeFi ecosystem featuring on-chain games, protocol-owned liquidity, and cross-chain expansion from Radix to Base and Solana.",
    iconUrl: "https://www.reddicks.meme/images/logo/logo_white.png",
    infoUrl: "https://REDDICKS.meme",
    divisibility: "18",
  },
  [SWAP_WIDGET_LSULP_RESOURCE_ADDRESS]: {
    resourceAddress: SWAP_WIDGET_LSULP_RESOURCE_ADDRESS,
    symbol: "LSULP",
    name: "LSU Pool LP",
    description: "Liquidity token for the LSU Pool",
    iconUrl: "https://assets.caviarnine.com/tokens/lsu_lp.png",
    infoUrl: "",
    divisibility: "18",
  },
};

export const SWAP_EXPLORER_ASSETS = [
  {
    symbol: "REDDICKS",
    name: "Reddicks",
    resourceAddress: REDDICKS_RESSOURCE_ADDRESS,
    accentColor: "#ff5b67",
    chartSupported: true,
    defaultQuoteSymbols: ["LSULP", "XRD", "CAVIAR"],
    featuredQuoteSymbols: ["LSULP", "XRD", "CAVIAR"],
    summary:
      "Project token driving the wider REDDICKS utility loop across the site, the treasury, and DckSlap.",
  },
  {
    symbol: "XRD",
    name: "Radix",
    resourceAddress: RADIX_TOKEN_RESOURCE_ADDRESS,
    accentColor: "#edf7ff",
    chartSupported: true,
    defaultQuoteSymbols: ["REDDICKS", "CAVIAR", "LSULP"],
    featuredQuoteSymbols: [],
    summary:
      "Core settlement asset of the Radix network and the cleanest quote leg for a general market view.",
  },
  {
    symbol: "CAVIAR",
    name: "CaviarNine",
    resourceAddress:
      "resource_rdx1tkk83magp3gjyxrpskfsqwkg4g949rmcjee4tu2xmw93ltw2cz94sq",
    accentColor: "#f5c84b",
    chartSupported: true,
    defaultQuoteSymbols: ["XRD", "REDDICKS"],
    featuredQuoteSymbols: ["XRD"],
    summary:
      "CaviarNine's native token and a useful Radix-native comparison leg beside REDDICKS and XRD.",
  },
  {
    symbol: "LSULP",
    name: "LSULP",
    resourceAddress: SWAP_WIDGET_LSULP_RESOURCE_ADDRESS,
    accentColor: "#73c8ff",
    chartSupported: true,
    defaultQuoteSymbols: ["REDDICKS", "XRD"],
    featuredQuoteSymbols: [],
    summary:
      "The official widget market against REDDICKS and a yield-bearing Radix reference pair.",
  },
  {
    symbol: "DCKSLAP",
    name: "DCKSLAP",
    resourceAddress: DCKSLAP_RESOURCE_ADDRESS,
    accentColor: "#a78bfa",
    chartSupported: false,
    defaultQuoteSymbols: ["REDDICKS", "XRD"],
    featuredQuoteSymbols: [],
    summary:
      "Game utility tracked across the DckSlap loop. Included in the explorer registry even when chart support is not guaranteed.",
  },
  {
    symbol: "GBOF",
    name: "Great Balls of Fire",
    resourceAddress: GBOF_RESOURCE_ADDRESS,
    accentColor: "#fb923c",
    chartSupported: false,
    defaultQuoteSymbols: ["REDDICKS", "XRD"],
    featuredQuoteSymbols: [],
    summary:
      "Reward token emitted by DckSlap activity. Useful for utility context even when price-chart coverage is limited.",
  },
];

const assetByAddress = SWAP_EXPLORER_ASSETS.reduce((registry, asset) => {
  registry[asset.resourceAddress] = asset;
  return registry;
}, {});

const assetBySymbol = SWAP_EXPLORER_ASSETS.reduce((registry, asset) => {
  registry[asset.symbol] = asset;
  return registry;
}, {});

const uniqueAssets = (assets) => {
  const seen = new Set();

  return assets.filter((asset) => {
    if (!asset?.resourceAddress || seen.has(asset.resourceAddress)) {
      return false;
    }

    seen.add(asset.resourceAddress);
    return true;
  });
};

export const getSwapExplorerAssetByAddress = (resourceAddress) =>
  assetByAddress[resourceAddress] || null;

export const getSwapExplorerAssetBySymbol = (symbol) => assetBySymbol[symbol] || null;

export const getSwapExplorerQuoteCandidates = (baseResourceAddress, currentQuoteResource = null) => {
  const baseAsset = getSwapExplorerAssetByAddress(baseResourceAddress);
  const preferredQuoteAssets = (baseAsset?.defaultQuoteSymbols || [])
    .map((symbol) => getSwapExplorerAssetBySymbol(symbol))
    .filter(Boolean);
  const currentQuoteAsset = getSwapExplorerAssetByAddress(currentQuoteResource);
  const chartFallbackAssets = SWAP_EXPLORER_ASSETS.filter(
    (asset) => asset.chartSupported && asset.resourceAddress !== baseResourceAddress
  );

  return uniqueAssets([
    ...preferredQuoteAssets,
    currentQuoteAsset,
    ...chartFallbackAssets,
  ]).filter((asset) => asset.resourceAddress !== baseResourceAddress);
};

export const getSwapExplorerFeaturedPairs = () => {
  const seen = new Set();

  return SWAP_EXPLORER_ASSETS.flatMap((asset) =>
    (asset.featuredQuoteSymbols || []).map((quoteSymbol) => ({
      baseAsset: asset,
      quoteAsset: getSwapExplorerAssetBySymbol(quoteSymbol),
    }))
  )
    .filter(({ baseAsset, quoteAsset }) => {
      if (!baseAsset?.chartSupported || !quoteAsset?.chartSupported) {
        return false;
      }

      const pairKey = `${baseAsset.resourceAddress}::${quoteAsset.resourceAddress}`;
      if (seen.has(pairKey)) {
        return false;
      }

      seen.add(pairKey);
      return true;
    })
    .slice(0, 6);
};

export const isSwapExplorerPairChartSupported = (leftResourceAddress, rightResourceAddress) => {
  if (!leftResourceAddress || !rightResourceAddress || leftResourceAddress === rightResourceAddress) {
    return false;
  }

  const leftAsset = getSwapExplorerAssetByAddress(leftResourceAddress);
  const rightAsset = getSwapExplorerAssetByAddress(rightResourceAddress);

  return Boolean(leftAsset?.chartSupported && rightAsset?.chartSupported);
};

export const getSwapExplorerChartUrl = (leftResourceAddress, rightResourceAddress) => {
  const url = new URL(RADIX_CHARTS_URL);
  url.searchParams.set("left", leftResourceAddress);
  url.searchParams.set("right", rightResourceAddress);
  url.searchParams.set("chart", "true");
  return url.toString();
};

export const getSwapWidgetSyncConfig = (baseResourceAddress, quoteResourceAddress) => {
  const isExactMarket =
    baseResourceAddress === REDDICKS_RESSOURCE_ADDRESS &&
    quoteResourceAddress === SWAP_WIDGET_LSULP_RESOURCE_ADDRESS;
  const isSameMarketReversed =
    baseResourceAddress === SWAP_WIDGET_LSULP_RESOURCE_ADDRESS &&
    quoteResourceAddress === REDDICKS_RESSOURCE_ADDRESS;

  if (!isExactMarket && !isSameMarketReversed) {
    return null;
  }

  return {
    mode: isExactMarket ? "exact" : "pair-only",
    inputToken: SWAP_WIDGET_SYNC_TOKENS[REDDICKS_RESSOURCE_ADDRESS],
    outputToken: SWAP_WIDGET_SYNC_TOKENS[SWAP_WIDGET_LSULP_RESOURCE_ADDRESS],
  };
};
